import { useRouteMatcher, useRoutes, useRoutesMatch, useUrlGenerator } from '@folklore/routes';

import useCollection from './useCollection';
import useDocument from './useDocument';
import useDocumentIsTrailer from './useDocumentIsTrailer';

// What the timeline layout needs to know about its current page: its document or
// collection, and how the layout presents it (zoom, header, theme).
function useTimelineCurrentPage(pathname: string | null) {
    const routes = useRoutes();
    const routeMatcher = useRouteMatcher();
    const url = useUrlGenerator();

    const [, documentRouteParams] = routeMatcher(routes.document, pathname);
    const [, collectionRouteParam] = routeMatcher(routes.collection, pathname);
    const { slug: documentParam = null } = documentRouteParams || {};
    const { slug: collectionParam = null } = collectionRouteParam || {};

    const { document = null, isFetching: documentIsFetching = false } = useDocument(documentParam, {
        enabled: documentParam !== null,
        placeholderData: null,
    });
    const { collection = null } = useCollection(collectionParam, {
        enabled: collectionParam !== null,
        placeholderData: null,
    });
    const { type: documentType = null, theme: documentTheme } = document || {};
    const { fullscreen: documentIsFullscreen = false } = documentTheme || {};
    const withoutNext =
        document?.metadata?.withoutNext === true || collection?.metadata?.withoutNext === true;

    const isImmersive = useRoutesMatch(['micromag', 'video', 'podcast'], pathname);
    const isHomePage = useRoutesMatch(['home'], pathname);
    const isProPage = useRoutesMatch(['pro'], pathname);
    const isMicromagAllPage = useRoutesMatch(['micromags.all'], pathname);
    const isMicromagPage = useRoutesMatch(['micromag'], pathname) && !isMicromagAllPage;
    const isAuthPage = useRoutesMatch(['login', 'auth.wildcard', 'account.wildcard'], pathname);
    const isSubscriptionPage = useRoutesMatch(
        ['subscription.friend', 'subscription', 'subscribe'],
        pathname,
    );
    const isVideosPage =
        useRoutesMatch(['videos', 'videos.all'], pathname) || pathname === url('series');
    const isPodcastsPage = useRoutesMatch(['podcasts', 'podcasts.all'], pathname);
    const isZoomedInSection = useRoutesMatch(['map.wildcard', 'micromags'], pathname);

    const isTrailer = useDocumentIsTrailer(document, pathname);

    const initialZoomedIn =
        isZoomedInSection ||
        isHomePage ||
        isAuthPage ||
        isProPage ||
        isSubscriptionPage ||
        (isMicromagPage && !isTrailer) ||
        documentIsFullscreen;

    let theme = null;
    if (isVideosPage) {
        theme = 'videos';
    } else if (isPodcastsPage) {
        theme = 'podcasts';
    }

    return {
        document,
        documentType,
        documentIsFetching,
        documentIsFullscreen,
        withoutNext,
        isImmersive,
        isHomePage,
        isAuthPage,
        isSubscriptionPage,
        isMicromagPage,
        isTrailer,
        initialZoomedIn,
        withoutLogoInHeader: isHomePage || isProPage || isAuthPage || isSubscriptionPage,
        withSlogan: isAuthPage || isSubscriptionPage,
        withoutBumperAd: isHomePage || isAuthPage || isSubscriptionPage,
        theme,
    };
}

export default useTimelineCurrentPage;
